import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { CommandPalette, type PaletteScope } from './CommandPalette';

/**
 * Who may open the command palette, and in which scope (DX-306).
 *
 * The palette is the portal's one search (DX-341). The bar's button and ⌘K
 * open it, and so does a page that offers a search of its own — the docs hub's
 * field opens it under Docs, so a reader searching there gets the same
 * results, the same keys and the same window as everywhere else, rather than a
 * second search to learn.
 *
 * It lives here, above the bar and the page, because both need it, and it is
 * drawn here rather than inside the bar: the bar is a box `position: fixed`
 * elements are placed in once anything gives it a filter (TopBar.tsx says
 * why), and the palette is one.
 */
type Palette = {
  open: (scope?: PaletteScope) => void;
  toggle: () => void;
};

const PaletteContext = createContext<Palette>({
  open: () => undefined,
  toggle: () => undefined,
});

export const usePalette = () => useContext(PaletteContext);

export const PaletteProvider = ({ children }: PropsWithChildren<{}>) => {
  // The scope it was opened in, or null while it is closed.
  const [openIn, setOpenIn] = useState<PaletteScope | null>(null);
  const open = useCallback(
    (scope: PaletteScope = 'All') => setOpenIn(scope),
    [],
  );
  const toggle = useCallback(
    () => setOpenIn(current => (current ? null : 'All')),
    [],
  );
  const value = useMemo(() => ({ open, toggle }), [open, toggle]);

  return (
    <PaletteContext.Provider value={value}>
      {children}
      {openIn && (
        <CommandPalette initialScope={openIn} onClose={() => setOpenIn(null)} />
      )}
    </PaletteContext.Provider>
  );
};
